import { createContext, useContext, useState, useEffect } from 'react';

const PWAContext = createContext();

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

// Check and persist app mode
const checkAppMode = () => {
  // Check URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const hasAppParam = urlParams.get('app') === 'true';
  
  // If app=true in URL, store it
  if (hasAppParam) {
    localStorage.setItem('nakshatra_app_mode', 'true');
    return true;
  }
  
  // Check localStorage for previously set app mode
  const storedAppMode = localStorage.getItem('nakshatra_app_mode') === 'true';
  if (storedAppMode) {
    return true;
  }
  
  // Check display mode
  const isDisplayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = window.navigator.standalone === true;
  const isAndroidTWA = document.referrer.includes('android-app://');
  
  // TWA detection
  const isTWA = document.referrer === '' && 
                !window.matchMedia('(display-mode: browser)').matches &&
                'getInstalledRelatedApps' in navigator;
  
  // Check if no browser chrome (fullscreen app)
  const isFullscreenApp = window.innerHeight >= screen.height - 150;
  
  // Persist only reliable standalone signals; the fullscreen heuristic is per-session
  if (isDisplayStandalone || isIOSStandalone || isAndroidTWA || isTWA) {
    localStorage.setItem('nakshatra_app_mode', 'true');
    return true;
  }
  
  return isFullscreenApp;
};

export const PWAProvider = ({ children }) => {
  // Initialize with check result immediately to prevent flicker
  const [isStandalone, setIsStandalone] = useState(() => checkAppMode());
  const [isIOS, setIsIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Re-check app mode on mount
    const isAppMode = checkAppMode();
    setIsStandalone(isAppMode);
    
    console.log('PWA Detection Result:', isAppMode);

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => {
      const newMode = checkAppMode();
      setIsStandalone(newMode);
    };
    mediaQuery.addEventListener('change', handleChange);

    // Check iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => {
      localStorage.setItem('nakshatra_app_mode', 'true');
      setIsStandalone(true);
      setDeferredPrompt(null);
    });
    
    // Re-check on resize (helps with TWA detection)
    window.addEventListener('resize', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('resize', handleChange);
    };
  }, []);

  const promptInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsStandalone(true);
      }
      setDeferredPrompt(null);
      return outcome === 'accepted';
    }
    return false;
  };

  return (
    <PWAContext.Provider value={{ 
      isStandalone, 
      isIOS, 
      canInstall: !!deferredPrompt,
      promptInstall 
    }}>
      {children}
    </PWAContext.Provider>
  );
};

export default PWAProvider;
